import { LiftOperator } from '../../liftoperator';
import {
    AggregatedTicket,
    AggregatedTicketSearchParams,
    convertAggregatedTicketSearchParamsToBackend,
} from '../../aggregatedTicket';
import {
    convertOrderTicketToFrontend,
    groupOrderTicketsByTicketName,
    OrderTicket,
    OrderTicketCollection,
} from '../../orderTicket';
import { mapProductToFrontend, Product } from '../../product';
import { SalesStatistic, Environment } from '../../environment';
import {
    convertIsoDateFromDate,
    type BackendType,
    buildSearchParams,
    urlBuilder,
} from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';

type GetStatisticsParams = { environments?: Environment[] };

type ProductDetailsResponse = {
    products: OrderTicket<BackendType>[];
};

export const liftOperatorUrls = {
    liftOperator: urlBuilder('liftoperators', `liftoperators`),
    aggregatedTicket: urlBuilder('order', 'liftoperators/tickets-aggregated'),
    liftOperatorStatistics: urlBuilder('order', 'liftoperators/statistics'),
};

/**
 * Type definition for the API with injected LiftOperator hooks
 */
export interface LiftOperatorApiHooks {
    useGetLiftOperatorsQuery: () => {
        data?: LiftOperator[];
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    useGetLiftOperatorQuery: (params: { liftOperatorId: string }) => {
        data?: LiftOperator;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    usePutLiftOperatorMutation: () => [
        (liftOperator: LiftOperator) => { unwrap: () => Promise<LiftOperator> },
        { isLoading: boolean; error?: unknown },
    ];
    useLazyGetAggregatedTicketsQuery: () => [
        (params?: AggregatedTicketSearchParams) => {
            unwrap: () => Promise<AggregatedTicket[]>;
        },
        {
            data?: AggregatedTicket[];
            isUninitialized: boolean;
            isFetching: boolean;
            isLoading: boolean;
            error?: unknown;
        },
    ];
    useGetStatisticsQuery: (params?: GetStatisticsParams) => {
        data?: SalesStatistic;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    useLazyGetProductDetailsQuery: () => [
        (params: { liftOperatorId: string; startDay: Date }) => {
            unwrap: () => Promise<OrderTicketCollection[]>;
        },
        {
            data?: OrderTicketCollection[];
            isFetching: boolean;
            isLoading: boolean;
            error?: unknown;
        },
    ];
    useGetLiftOperatorProductsQuery: (
        params: { liftOperatorId: string },
        options: { skip?: boolean }
    ) => {
        data?: Product[];
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
}

/**
 * LiftOperator endpoint definitions
 */
export const liftOperatorEndpoints = (builder: EntityEndpointBuilder) => ({
    getLiftOperators: builder.query<LiftOperator[], void>({
        query: () => `${liftOperatorUrls.liftOperator}?size=1000`,
        transformResponse: (response: { content: LiftOperator[] }) => {
            return response.content;
        },
        providesTags: ['liftOperator'],
    }),
    getLiftOperator: builder.query<LiftOperator, { liftOperatorId: string }>({
        query: ({ liftOperatorId }) =>
            `${liftOperatorUrls.liftOperator}/${liftOperatorId}`,
        providesTags: ['liftOperator'],
    }),
    putLiftOperator: builder.mutation<LiftOperator, LiftOperator>({
        query: (liftOperator) => ({
            method: 'PUT',
            url: `${liftOperatorUrls.liftOperator}/${liftOperator.id}`,
            body: liftOperator,
        }),
        invalidatesTags: ['liftOperator'],
    }),
    getAggregatedTickets: builder.query<
        AggregatedTicket[],
        AggregatedTicketSearchParams | void
    >({
        query: (params: AggregatedTicketSearchParams = {}) =>
            `${liftOperatorUrls.aggregatedTicket}${buildSearchParams(
                convertAggregatedTicketSearchParamsToBackend(params)
            )}`,
        transformResponse: (response: {
            aggregatedTickets: AggregatedTicket[];
        }) => {
            return response.aggregatedTickets;
        },
        providesTags: ['liftOperator'],
    }),
    getStatistics: builder.query<SalesStatistic, GetStatisticsParams | void>({
        query: (params: GetStatisticsParams = {}) =>
            `${liftOperatorUrls.liftOperatorStatistics}${buildSearchParams(params)}`,
        providesTags: ['liftOperator'],
    }),
    getProductDetails: builder.query<
        OrderTicketCollection[],
        {
            liftOperatorId: string;
            startDay: Date;
        }
    >({
        query: ({ liftOperatorId, startDay }) => {
            const searchParams = new URLSearchParams();

            if (startDay) {
                searchParams.append(
                    'startDay',
                    convertIsoDateFromDate(startDay)
                );
            }

            searchParams.append('environment', 'B2B');

            return `${
                liftOperatorUrls.liftOperator
            }/${liftOperatorId}/products-details?${searchParams.toString()}`;
        },
        transformResponse: (requestResponse: ProductDetailsResponse) => {
            const orderTickets = requestResponse.products.map(
                convertOrderTicketToFrontend
            );

            return groupOrderTicketsByTicketName(orderTickets);
        },
        providesTags: ['liftOperator'],
    }),
    getLiftOperatorProducts: builder.query<
        Product[],
        { liftOperatorId: string }
    >({
        query: ({ liftOperatorId }) =>
            urlBuilder(
                'liftoperators',
                `liftoperators/${liftOperatorId}/products`
            ),
        transformResponse: (response: { tickets: Product<BackendType>[] }) => {
            return response.tickets.map(mapProductToFrontend);
        },
        providesTags: ['liftOperator'],
    }),
});

export const liftOperatorApi = createEntityApi({
    tagTypes: ['liftOperator'],
    endpoints: liftOperatorEndpoints,
});

// Export hooks with explicit types for TypeScript inference
export const useGetLiftOperatorsQuery: LiftOperatorApiHooks['useGetLiftOperatorsQuery'] =
    liftOperatorApi.useGetLiftOperatorsQuery;
export const useGetLiftOperatorQuery: LiftOperatorApiHooks['useGetLiftOperatorQuery'] =
    liftOperatorApi.useGetLiftOperatorQuery;
export const useGetLiftOperatorProductsQuery: LiftOperatorApiHooks['useGetLiftOperatorProductsQuery'] =
    liftOperatorApi.useGetLiftOperatorProductsQuery;
export const useLazyGetAggregatedTicketsQuery: LiftOperatorApiHooks['useLazyGetAggregatedTicketsQuery'] =
    liftOperatorApi.useLazyGetAggregatedTicketsQuery;
export const useLazyGetProductDetailsQuery: LiftOperatorApiHooks['useLazyGetProductDetailsQuery'] =
    liftOperatorApi.useLazyGetProductDetailsQuery;
export const useGetStatisticsQuery: LiftOperatorApiHooks['useGetStatisticsQuery'] =
    liftOperatorApi.useGetStatisticsQuery;
export const usePutLiftOperatorMutation: LiftOperatorApiHooks['usePutLiftOperatorMutation'] =
    liftOperatorApi.usePutLiftOperatorMutation;
